import { supabase } from '../lib/supabase'
import { construirFilaDesdeVista } from '../utils/cuentasPorPagar'
import { cargarDetalleAbastecimiento } from './abastecimiento'

// Acceso a Supabase de Cuentas por Pagar.
//  - Lectura: public.vw_cuentas_por_pagar (saldos y estados los calcula
//    Supabase) y public.pagos_compras (historial).
//  - Escritura: SOLO con RPC. React nunca inserta ni modifica pagos ni
//    escribe en gastos.

export const PAGOS_COMPRAS_DISPONIBLES = true

const TAMANIO_PAGINA_CONSULTA = 1000
const MAXIMO_PAGINAS = 20

async function ejecutarRpc(nombre, parametros) {
  const { data, error } = await supabase.rpc(nombre, parametros)

  if (error) throw error

  return data
}

// Devuelve el UUID del pago. Reintentar con la MISMA clave es seguro:
// Supabase devuelve el pago original en vez de crear otro.
export function registrarPagoCompra({
  empresaId,
  compraId,
  fecha,
  monto,
  medioPago,
  referencia,
  tipoRespaldo,
  numeroDocumento,
  observaciones,
  claveIdempotencia,
}) {
  return ejecutarRpc('registrar_pago_compra', {
    p_empresa_id: empresaId,
    p_compra_id: compraId,
    p_fecha_pago: fecha,
    p_monto: monto,
    p_medio_pago: medioPago,
    p_referencia: referencia?.trim() || null,
    p_tipo_respaldo: tipoRespaldo,
    p_numero_documento: numeroDocumento?.trim() || null,
    p_observaciones: observaciones?.trim() || null,
    p_clave_idempotencia: claveIdempotencia,
  })
}

export function anularPagoCompra({ empresaId, pagoId, motivo }) {
  return ejecutarRpc('anular_pago_compra', {
    p_empresa_id: empresaId,
    p_pago_id: pagoId,
    p_motivo: motivo.trim(),
  })
}

export function asociarDocumentoPagoCompra({
  empresaId,
  pagoId,
  tipoRespaldo,
  numeroDocumento,
}) {
  return ejecutarRpc('asociar_documento_pago_compra', {
    p_empresa_id: empresaId,
    p_pago_id: pagoId,
    p_tipo_respaldo: tipoRespaldo,
    p_numero_documento: numeroDocumento?.trim() || null,
  })
}

async function cargarFilasVista(empresaId, compraId) {
  const filas = []

  for (let pagina = 0; pagina < MAXIMO_PAGINAS; pagina += 1) {
    const desde = pagina * TAMANIO_PAGINA_CONSULTA

    let consulta = supabase
      .from('vw_cuentas_por_pagar')
      .select('*')
      .eq('empresa_id', empresaId)

    if (compraId) consulta = consulta.eq('compra_id', compraId)

    const { data, error } = await consulta
      .order('fecha_compra', { ascending: false })
      .order('numero_compra', { ascending: false })
      .range(desde, desde + TAMANIO_PAGINA_CONSULTA - 1)

    if (error) throw error

    filas.push(...(data || []))

    if (!data || data.length < TAMANIO_PAGINA_CONSULTA) {
      return { filas, truncado: false }
    }
  }

  return { filas, truncado: true }
}

// Todas las cuentas de la empresa (los filtros se aplican en pantalla para
// que indicadores y paginación respeten los mismos filtros).
export async function cargarCuentasPorPagar(empresaId) {
  const { filas, truncado } = await cargarFilasVista(empresaId)

  return { filas: filas.map(construirFilaDesdeVista), truncado }
}

// Historial completo, incluidos los pagos anulados (que no suman).
export async function cargarPagosCompra(empresaId, compraId) {
  const { data, error } = await supabase
    .from('pagos_compras')
    .select(
      'id, fecha_pago, monto, medio_pago, referencia, tipo_respaldo, numero_documento, observaciones, estado, creado_por, creado_en, anulado_por, anulado_en, motivo_anulacion, documento_actualizado_por, documento_actualizado_en'
    )
    .eq('empresa_id', empresaId)
    .eq('compra_id', compraId)
    .order('fecha_pago', { ascending: false })
    .order('creado_en', { ascending: false })

  if (error) throw error

  const pagos = data || []
  const usuarioIds = [
    ...new Set(
      pagos.flatMap((pago) =>
        [pago.creado_por, pago.anulado_por].filter(Boolean)
      )
    ),
  ]

  let nombres = new Map()

  if (usuarioIds.length > 0) {
    const { data: perfiles } = await supabase
      .from('perfiles')
      .select('id, nombre_completo, correo')
      .in('id', usuarioIds)

    nombres = new Map(
      (perfiles || []).map((perfil) => [
        perfil.id,
        perfil.nombre_completo || perfil.correo || '—',
      ])
    )
  }

  return pagos.map((pago) => ({
    ...pago,
    creado_por_nombre: nombres.get(pago.creado_por) || '—',
    anulado_por_nombre: nombres.get(pago.anulado_por) || null,
  }))
}

// `fila` es null cuando la compra no es cuenta por pagar (anulada o con
// recepción rechazada): la vista no la incluye.
export async function cargarDetalleCuentaPorPagar(empresaId, compraId) {
  const [detalle, vista, pagos] = await Promise.all([
    cargarDetalleAbastecimiento(empresaId, compraId),
    cargarFilasVista(empresaId, compraId),
    cargarPagosCompra(empresaId, compraId),
  ])

  if (!detalle) return null

  return {
    compra: detalle.compra,
    recepcion: detalle.recepcion,
    fila: vista.filas[0] ? construirFilaDesdeVista(vista.filas[0]) : null,
    pagos,
  }
}
